import { useState, useEffect } from 'react';

export default function Hero() {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const targetDate = new Date('2025-12-31T18:00:00').getTime();

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const difference = targetDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-red-600 via-red-500 to-orange-500">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/4e13e245b699d897c29271f5db698194.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
        <div className="mb-8">
          <h1 className="text-5xl md:text-7xl font-bold mb-4" style={{ fontFamily: '"Pacifico", serif' }}>
            XX San Silvestre
          </h1>
          <h2 className="text-3xl md:text-5xl font-bold mb-2">
            Zaragoza El Rincón 2025
          </h2>
          <p className="text-xl md:text-2xl font-medium opacity-90">
            ¡Despide el año corriendo por las calles de Zaragoza!
          </p>
        </div>

        {/* Event Details */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8 max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mb-3">
                <i className="ri-calendar-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold mb-1">Fecha</h3>
              <p className="text-white/90">31 de Diciembre 2025</p>
              <p className="text-white/90">18:00 horas</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mb-3">
                <i className="ri-map-pin-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold mb-1">Salida</h3>
              <p className="text-white/90">Calle Coso (Teatro Principal)</p>
              <p className="text-white/90">Cerca de Plaza España</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mb-3">
                <i className="ri-route-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold mb-1">Distancia</h3>
              <p className="text-white/90">5 Kilómetros</p>
              <p className="text-white/90">Por el centro histórico</p>
            </div>
          </div>
        </div>

        {/* Countdown */}
        <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 mb-8 max-w-2xl mx-auto">
          <h3 className="text-2xl font-bold mb-4">¡Cuenta atrás para la carrera!</h3>
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold">{timeLeft.days}</div>
              <div className="text-sm opacity-80">Días</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold">{timeLeft.hours}</div>
              <div className="text-sm opacity-80">Horas</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold">{timeLeft.minutes}</div>
              <div className="text-sm opacity-80">Minutos</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold">{timeLeft.seconds}</div>
              <div className="text-sm opacity-80">Segundos</div>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a 
            href="https://www.avaibooksports.com/inscripcion/xx-san-silvestre-zaragoza-el-rincon-2025/inscripcion_datos/" 
            target="_blank"
            rel="noopener noreferrer"
            className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-user-add-line mr-2"></i>
            ¡Inscríbete Ahora!
          </a>
          <a 
            href="#informacion" 
            className="bg-white/20 hover:bg-white/30 text-white border-2 border-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-information-line mr-2"></i>
            Más Información
          </a>
        </div>

        {/* Costume Highlight */}
        <div className="mt-12 bg-yellow-400/20 backdrop-blur-sm rounded-2xl p-6 max-w-3xl mx-auto">
          <div className="flex items-center justify-center mb-3">
            <i className="ri-shirt-line text-3xl text-yellow-300 mr-3"></i>
            <h3 className="text-2xl font-bold text-yellow-300">¡Ven disfrazado!</h3>
          </div>
          <p className="text-lg text-white/90">
            Saca tus mejores dotes de creatividad y diseña tu disfraz más original. 
            ¡Hagamos de esta San Silvestre la más divertida de todas!
          </p>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <i className="ri-arrow-down-line text-3xl text-white/70"></i>
      </div>
    </section>
  );
}